// Right-column canvas view. Renders each pinned item as a KernelBlock
// inside a captioned card. The user can remove individual items via the
// × button on each card and clear the whole canvas via the header
// "clear" affordance — those mutations flow back through onRemove /
// onClear callbacks so the canvas state stays consistent with the
// agent-side view (canvas_inspect would see the same items).
//
// The component stays mounted whether or not the canvas has items so
// the open/close states can transition via CSS width animation. The
// parent (ChatConsole) controls the column width via a grid template
// transition; this component only handles its border + opacity fade.
//
// Tree shape is deliberate so PNG export captures the WHOLE canvas
// (header + all pinned items at their natural sizes), not the visible
// scroll viewport:
//   aside              — column-animation clipping (overflow-hidden)
//   └─ scroll wrapper  — h-full + overflow-y-auto (lets user scroll)
//      └─ exportRoot   — flex column at natural height, target of
//                        triggerExportPng. Per-card buttons + the
//                        header's action row carry data-export-ignore
//                        so they're stripped from the PNG.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KernelBlock } from "./kernelPrimitives/index.jsx";
import KernelWidget from "./KernelWidget.jsx";
import { triggerExportPng } from "./ExportableBlock.jsx";
import OverlayDialog from "./OverlayDialog.jsx";
import {
    kernelResolverForTenant,
    resolveKernelByCommandAsync,
    subscribeKernels,
} from "../lib/kernelStore.js";
import {
    RUN_SURFACES,
    createLiveRunContext,
} from "../lib/kernelResolver.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

const DEFAULT_WIDTH_CSS = "min(480px,40vw)";
const LIVE_CHAT_RUN_CONTEXT = createLiveRunContext({
    surface: RUN_SURFACES.CHAT,
});

export default function ChatCanvas({
    canvas,
    open,
    width,
    onRemove,
    onClear,
    onOpenScope,
    linkifyProse,
    onSaveAsKernels,
    onSaveAsCanvas,
    onViewCanvas,
    onViewKernel,
    onRetryItem,
    onRebuildItem,
    suggestCanvasName,
    saveStatusByItem,
}) {
    const items = canvas?.items || [];
    const innerWidth = width != null ? `${width}px` : DEFAULT_WIDTH_CSS;

    const exportRef = useRef(null);
    const [exporting, setExporting] = useState(false);
    const handleExport = useCallback(async () => {
        if (exporting) return;
        setExporting(true);
        try {
            await triggerExportPng(exportRef.current, `canvas-${Date.now()}.png`);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[canvas] export failed:", err);
        } finally {
            setExporting(false);
        }
    }, [exporting]);

    // Per-card status helper (running / saved / error). The transient
    // `saveStatusByItem` overlay wins over the persistent
    // `item.savedKernel` so an in-flight save reflects the latest
    // outcome even when the persistent badge would say "saved".
    const statusFor = (item) => {
        const transient = saveStatusByItem?.[item.id];
        if (transient) return transient;
        if (item.savedKernel?.command) {
            return { status: "saved", command: item.savedKernel.command };
        }
        return null;
    };
    const anyRunning = items.some((it) => saveStatusByItem?.[it.id]?.status === "running");
    const allSaved = items.length > 0 && items.every((it) => it.savedKernel?.command);

    // ── Canvas-save (template) modal ─────────────────────────────
    // Click "↓ canvas" → OverlayDialog opens with the same shape
    // used elsewhere in the dashboard (Invite user, etc.). Eyebrow
    // + title + fields + green action button. Escape and backdrop
    // click dismiss; the dialog state is local to the canvas so it
    // doesn't leak into chat persistence.
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const openSaveDialog = () => setSaveDialogOpen(true);
    const closeSaveDialog = () => setSaveDialogOpen(false);
    const seedName = useMemo(() => (
        typeof suggestCanvasName === "function" ? (suggestCanvasName() || "") : ""
    ), [saveDialogOpen, suggestCanvasName]); // eslint-disable-line react-hooks/exhaustive-deps

    // Post-save banner. Set by SaveCanvasDialog's onSaved callback;
    // auto-dismisses after ~8s OR when the user clicks dismiss / view.
    // Holds the just-saved template so "view canvas" can deep-link.
    const [justSaved, setJustSaved] = useState(null); // { slug, title } | null
    useEffect(() => {
        if (!justSaved) return undefined;
        const timer = setTimeout(() => setJustSaved(null), 8000);
        return () => clearTimeout(timer);
    }, [justSaved]);
    const handleViewSaved = () => {
        if (justSaved?.slug && onViewCanvas) onViewCanvas(justSaved.slug);
        setJustSaved(null);
    };

    // ── Per-card live test ───────────────────────────────────────
    // Per-item "▶ test" expands a live KernelWidget below the frozen
    // render so the user can compare the saved kernel's fresh output
    // against the snapshot that was pinned. State lives here (not on
    // the card) so toggling one card doesn't reset another's state.
    const [liveByItem, setLiveByItem] = useState({}); // { itemId: true/false }
    const [liveLoadByItem, setLiveLoadByItem] = useState({});
    const toggleLive = async (item) => {
        if (liveByItem[item.id]) {
            setLiveByItem((prior) => ({ ...prior, [item.id]: false }));
            return;
        }
        const command = item.savedKernel?.command;
        if (!command) return;
        setLiveLoadByItem((prior) => ({ ...prior, [item.id]: { loading: true } }));
        try {
            await resolveKernelByCommandAsync(null, command);
            setLiveByItem((prior) => ({ ...prior, [item.id]: true }));
            setLiveLoadByItem((prior) => ({ ...prior, [item.id]: null }));
        } catch (error) {
            setLiveLoadByItem((prior) => ({ ...prior, [item.id]: { error } }));
        }
    };
    // Per-card rebuild mode + feedback draft. Lives here so toggling
    // one card doesn't reset another's draft.
    const [rebuildByItem, setRebuildByItem] = useState({}); // { itemId: { feedback } }
    const openRebuild = (itemId) => setRebuildByItem((p) => ({ ...p, [itemId]: { feedback: "" } }));
    const closeRebuild = (itemId) => setRebuildByItem((p) => {
        const next = { ...p };
        delete next[itemId];
        return next;
    });
    const setRebuildFeedback = (itemId, value) => setRebuildByItem((p) => ({
        ...p,
        [itemId]: { ...(p[itemId] || {}), feedback: value },
    }));
    const submitRebuild = (itemId) => {
        if (!onRebuildItem) return;
        const draft = rebuildByItem[itemId];
        onRebuildItem(itemId, draft?.feedback || "");
        closeRebuild(itemId);
    };

    // Live kernel lookup — we need each saved kernel's full
    // definition to render via KernelWidget. Keep the shared resolver,
    // refresh on kernel-store subscription so a freshly re-saved
    // kernel shows its new shape immediately.
    const [kernelResolver, setKernelResolver] = useState(() => currentKernelResolver());
    useEffect(() => {
        return subscribeKernels(() => setKernelResolver(currentKernelResolver()));
    }, []);

    return (
        <aside
            className={`relative flex h-full w-full min-w-0 flex-col overflow-hidden bg-[color:var(--panel-soft)] transition-[opacity] duration-300 ease-out ${
                open
                    ? "border-l border-[color:var(--line)] opacity-100"
                    : "border-l-0 opacity-0"
            }`}
            aria-label="Canvas"
            aria-hidden={!open}
        >
            <div className="h-full overflow-y-auto" style={{ width: innerWidth }}>
                <div ref={exportRef} className="flex flex-col">
                    <header className="flex items-center justify-between gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[8px]">
                        <span className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                            Canvas · {items.length} item{items.length === 1 ? "" : "s"}
                        </span>
                        <div className="flex items-baseline gap-[10px]" data-export-ignore="true">
                            <button
                                type="button"
                                onClick={onSaveAsKernels}
                                disabled={!onSaveAsKernels || items.length === 0 || anyRunning}
                                className="bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Save each canvas item as a kernel"
                                aria-label="Save canvas items as kernels"
                            >
                                {anyRunning ? "saving…" : "↓ kernels"}
                            </button>
                            <button
                                type="button"
                                onClick={openSaveDialog}
                                disabled={!onSaveAsCanvas || items.length === 0 || anyRunning}
                                className="bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                title={allSaved
                                    ? "Save this canvas as a named template that reopens with live kernel data"
                                    : "Save the whole canvas as a named template (kernels will auto-save first if needed)"}
                                aria-label="Save canvas as a named template"
                            >
                                ↓ canvas
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={exporting || items.length === 0}
                                className="bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                                title="Export canvas as PNG"
                                aria-label="Export canvas as PNG"
                            >
                                {exporting ? "…" : "↓ png"}
                            </button>
                            <button
                                type="button"
                                onClick={onClear}
                                className="bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
                                title="Remove all canvas items"
                            >
                                clear
                            </button>
                        </div>
                    </header>
                    {justSaved ? (
                        <div
                            className="flex items-center gap-[10px] border-b border-[color:var(--line-soft)] bg-[color:var(--green-soft)] px-[12px] py-[8px] text-[11px] leading-[1.3] text-[color:var(--ink)]"
                            data-export-ignore="true"
                            role="status"
                            aria-live="polite"
                        >
                            <span
                                className="min-w-0 flex-1 truncate"
                                title={`Saved as /${justSaved.slug}`}
                            >
                                <span className="text-[color:var(--green)]" aria-hidden="true">✓ </span>
                                Saved as <span className="font-mono text-[color:var(--ink)]">/{justSaved.slug}</span>
                            </span>
                            <div className="flex shrink-0 items-baseline gap-[8px]">
                                <button
                                    type="button"
                                    onClick={handleViewSaved}
                                    className="cursor-pointer rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[2px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--ink)] hover:border-[color:var(--green)]"
                                >
                                    view
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setJustSaved(null)}
                                    className="cursor-pointer bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                                    title="Dismiss"
                                    aria-label="Dismiss"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ) : null}
                    <div className="flex flex-col gap-[12px] px-[12px] py-[12px]">
                        {items.map((item) => {
                            const st = statusFor(item);
                            const live = liveByItem[item.id];
                            const resolvedLiveKernel = item.savedKernel?.command
                                ? kernelResolver.resolveByCommand(item.savedKernel.command)
                                : null;
                            const liveKernel = resolvedLiveKernel?.body || null;
                            return (
                                <article
                                    key={item.id}
                                    className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[10px]"
                                >
                                    <header className="mb-[8px] flex items-baseline justify-between gap-[10px]">
                                        {item.caption ? (
                                            <p className="m-0 min-w-0 flex-1 text-[11px] leading-[1.4] text-[color:var(--muted)]">
                                                {item.caption}
                                            </p>
                                        ) : (
                                            <span className="min-w-0 flex-1 truncate text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                                                {item.spec?.kind || "render"}
                                            </span>
                                        )}
                                        {item.addedAt ? (
                                            <span
                                                className="shrink-0 font-mono text-[9px] tracking-normal text-[color:var(--muted-soft)]"
                                                title={`Pinned ${new Date(item.addedAt).toLocaleString()}. This chart is a snapshot. Click the saved label below to run it with current data.`}
                                            >
                                                pinned {formatAgo(item.addedAt)}
                                            </span>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={() => onRemove?.(item.id)}
                                            data-export-ignore="true"
                                            className="shrink-0 bg-transparent text-[12px] leading-[1] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)] cursor-pointer"
                                            title="Remove from canvas"
                                            aria-label={`Remove ${item.spec?.kind || "item"} from canvas`}
                                        >
                                            ×
                                        </button>
                                    </header>
                                    <KernelBlock spec={item.spec} data={item.data} onOpenScope={onOpenScope} linkifyText={linkifyProse} />
                                    {st ? (
                                        <footer className="mt-[8px] flex items-baseline justify-between gap-[10px] text-[10px]">
                                            {st.status === "saved" && item.savedKernel?.command ? (
                                                <div className="flex items-center gap-[8px] min-w-0">
                                                    {/* Saved badge IS the test-live toggle now
                                                        — the most expected click target on a
                                                        "saved as /command" label is "let me
                                                        see what it actually does." */}
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleLive(item)}
                                                        data-export-ignore="true"
                                                        className="flex items-center gap-[6px] min-w-0 cursor-pointer bg-transparent uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                                                        title={live ? "Hide current results" : "Run the saved kernel with current data"}
                                                        aria-pressed={live ? true : undefined}
                                                    >
                                                        <span className="text-[color:var(--green)]" aria-hidden="true">✓</span>
                                                        <span className="truncate">saved as <span className="font-mono normal-case tracking-normal text-[color:var(--ink)]">/{item.savedKernel.command}</span></span>
                                                        <span className="text-[color:var(--muted-soft)]" aria-hidden="true">
                                                            {liveLoadByItem[item.id]?.loading ? "…" : live ? "✕" : "▶"}
                                                        </span>
                                                    </button>
                                                    {onViewKernel ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => onViewKernel(item.savedKernel.command)}
                                                            data-export-ignore="true"
                                                            className="shrink-0 cursor-pointer bg-transparent text-[10px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                                                            title="Open this kernel in the Kernels admin"
                                                            aria-label="Open kernel in admin"
                                                        >
                                                            ↗
                                                        </button>
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <CardStatusBadge status={st} />
                                            )}
                                            {st.status === "error" && onRetryItem ? (
                                                <button
                                                    type="button"
                                                    onClick={() => onRetryItem(item.id)}
                                                    data-export-ignore="true"
                                                    className="shrink-0 cursor-pointer bg-transparent uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                                                    title="Re-run the save for this one item"
                                                >
                                                    ↻ retry
                                                </button>
                                            ) : null}
                                            {liveLoadByItem[item.id]?.error ? (
                                                <span role="alert" className="text-[10px] text-[color:var(--red,#c0392b)]">
                                                    Could not load /{item.savedKernel?.command}
                                                </span>
                                            ) : null}
                                        </footer>
                                    ) : null}
                                    {live && item.savedKernel?.command ? (
                                        <div className="mt-[10px] border-t border-dashed border-[color:var(--line)] pt-[10px]" data-export-ignore="true">
                                            <div className="mb-[6px] flex items-baseline justify-between gap-[8px]">
                                                <span className="text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                                                    live · /{item.savedKernel.command}
                                                </span>
                                                {liveKernel && onRebuildItem ? (
                                                    rebuildByItem[item.id] ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => closeRebuild(item.id)}
                                                            className="shrink-0 cursor-pointer bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                                                        >
                                                            ✕ cancel rebuild
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => openRebuild(item.id)}
                                                            className="shrink-0 cursor-pointer bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
                                                            title="Rebuild this kernel with optional feedback"
                                                        >
                                                            ↻ rebuild
                                                        </button>
                                                    )
                                                ) : null}
                                            </div>
                                            {liveKernel ? (
                                                <KernelWidget
                                                    kernel={liveKernel}
                                                    resolvedDefinition={resolvedLiveKernel}
                                                    runContext={LIVE_CHAT_RUN_CONTEXT}
                                                    fill={false}
                                                    showHeader={false}
                                                    onOpenScope={onOpenScope}
                                                />
                                            ) : (
                                                <div className="rounded-[2px] border border-dashed border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px] text-[10.5px] text-[color:var(--muted)]">
                                                    Kernel /{item.savedKernel.command} is missing. It may have been deleted. Save this canvas item again to restore it.
                                                </div>
                                            )}
                                            {rebuildByItem[item.id] ? (
                                                <div className="mt-[8px] flex flex-col gap-[6px]">
                                                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                                                        What was off? <span className="normal-case tracking-normal">(optional; leave blank to retry)</span>
                                                    </span>
                                                    <textarea
                                                        value={rebuildByItem[item.id]?.feedback || ""}
                                                        onChange={(e) => setRebuildFeedback(item.id, e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                                                e.preventDefault();
                                                                submitRebuild(item.id);
                                                            }
                                                            if (e.key === "Escape") {
                                                                e.preventDefault();
                                                                closeRebuild(item.id);
                                                            }
                                                        }}
                                                        rows={3}
                                                        placeholder="Example: The May 17 spike looks wrong. Try removing the cache-hit filter."
                                                        className="block w-full resize-y rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] text-[11px] leading-[1.4] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                                                    />
                                                    <div className="flex items-baseline gap-[6px]">
                                                        <button
                                                            type="button"
                                                            onClick={() => submitRebuild(item.id)}
                                                            className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[8px] py-[2px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--ink)] hover:border-[color:var(--green)]"
                                                        >
                                                            rebuild kernel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => closeRebuild(item.id)}
                                                            className="shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[2px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                                                        >
                                                            cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </article>
                            );
                        })}
                    </div>
                </div>
            </div>
            <SaveCanvasDialog
                open={saveDialogOpen}
                onClose={closeSaveDialog}
                onSubmit={onSaveAsCanvas}
                onSaved={(template) => setJustSaved({ slug: template.slug, title: template.title })}
                seedName={seedName}
                itemCount={items.length}
                items={items}
                saveStatusByItem={saveStatusByItem}
            />
        </aside>
    );
}

// Modeled on the Invite User dialog in UserManagementPage — same
// OverlayDialog shape, eyebrow + title, labelled fields with the
// dashboard's input styling, single green primary action at the
// bottom. Re-seeds its draft name from `seedName` each time the
// dialog opens so reopening after a successful save doesn't carry
// the prior input over.
function SaveCanvasDialog({ open, onClose, onSubmit, onSaved, seedName, itemCount, items, saveStatusByItem }) {
    const [name, setName] = useState(seedName || "");
    const [description, setDescription] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    // Re-seed on open. Done in effect (not derived state) so the
    // user can edit freely while the dialog is open without their
    // edits being clobbered by a re-render of the parent.
    useEffect(() => {
        if (open) {
            setName(seedName || "");
            setDescription("");
            setError(null);
            setBusy(false);
            setPhase(null);
            setPendingCount(0);
            setPendingIds(null);
            const raf = requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
            return () => cancelAnimationFrame(raf);
        }
        return undefined;
    }, [open, seedName]);

    // Two response shapes from onSubmit:
    //   • { ok, error?, template? }         — synchronous result
    //   • { promise, phase, pendingCount }  — async: kernels need to
    //                                          save first; promise
    //                                          resolves to the
    //                                          synchronous shape once
    //                                          they're all in.
    const [phase, setPhase] = useState(null); // "kernels" | "canvas" | null
    const [pendingCount, setPendingCount] = useState(0);
    // Captured at submit time so progress is measured against the set
    // we kicked off, not whatever the canvas currently looks like (an
    // unrelated cancel + re-pin would otherwise skew counts).
    const [pendingIds, setPendingIds] = useState(null);

    // Live progress: count how many of the captured-at-submit pending
    // items have now landed in each terminal state. saveStatusByItem
    // is the transient overlay; item.savedKernel is the persistent
    // success marker. Either counts as "saved".
    const progress = useMemo(() => {
        if (!pendingIds || phase !== "kernels") return null;
        const ids = [...pendingIds];
        const byId = new Map((items || []).map((it) => [it.id, it]));
        let saved = 0;
        let running = 0;
        let errored = 0;
        for (const id of ids) {
            const it = byId.get(id);
            const transient = saveStatusByItem?.[id];
            if (it?.savedKernel?.command || transient?.status === "saved") saved += 1;
            else if (transient?.status === "running") running += 1;
            else if (transient?.status === "error") errored += 1;
        }
        return { total: ids.length, saved, running, errored };
    }, [pendingIds, phase, items, saveStatusByItem]);
    const submit = (e) => {
        e?.preventDefault();
        if (busy) return;
        setBusy(true);
        const res = onSubmit?.({ name, description });
        if (res && typeof res.then === "function") {
            // raw Promise from a future caller; treat as canvas-phase.
            setPhase("canvas");
            res.then(handleResult).catch((err) => handleResult({ ok: false, error: err?.message || String(err) }));
            return;
        }
        if (res && res.promise) {
            // Kernels-then-canvas flow. Show phase progress.
            setPhase(res.phase || "kernels");
            setPendingCount(res.pendingCount || 0);
            // Snapshot which canvas items we triggered saves for so
            // the progress meter measures against THAT set, not
            // whatever the canvas currently contains (an unrelated
            // canvas mutation while saving would otherwise skew it).
            const pendingSnapshot = (items || [])
                .filter((it) => !it.savedKernel?.command)
                .map((it) => it.id);
            setPendingIds(new Set(pendingSnapshot));
            res.promise.then((finalRes) => {
                setPhase("canvas");
                // Tiny tick so the user sees "saving canvas" before it
                // disappears — feels less like a flicker.
                setTimeout(() => handleResult(finalRes), 100);
            }).catch((err) => handleResult({ ok: false, error: err?.message || String(err) }));
            return;
        }
        handleResult(res);
    };
    const handleResult = (res) => {
        if (res?.ok) {
            if (res.template) onSaved?.(res.template);
            setPhase(null);
            setBusy(false);
            onClose?.();
        } else if (res?.error) {
            setError(res.error);
            setPhase(null);
            setBusy(false);
        } else {
            setPhase(null);
            setBusy(false);
        }
    };

    return (
        <OverlayDialog
            open={open}
            onClose={onClose}
            analyticsId={DIALOG.CANVAS_SAVE}
            eyebrow="Canvas"
            title="Save canvas"
            width="480px"
        >
            <form onSubmit={submit} className="grid gap-[12px]">
                <label className="grid gap-[4px]">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">Name</span>
                    <input
                        ref={inputRef}
                        type="text"
                        value={name}
                        onChange={(e) => { setName(e.target.value); if (error) setError(null); }}
                        placeholder="canvas-name"
                        required
                        aria-invalid={error ? true : undefined}
                        className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] font-mono text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--line-strong)]"
                    />
                </label>
                <label className="grid gap-[4px]">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">Description <span className="normal-case tracking-normal text-[color:var(--muted-soft)]">(optional)</span></span>
                    <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="what's in this canvas"
                        className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--line-strong)]"
                    />
                </label>
                <span className="text-[10px] leading-[1.45] text-[color:var(--muted-soft)]">
                    Saves {itemCount} kernel{itemCount === 1 ? "" : "s"} as a named view. Open it later from Canvases to run each kernel with current data.
                </span>
                {phase === "kernels" && progress ? (
                    <KernelSaveProgress progress={progress} />
                ) : phase === "kernels" ? (
                    <span className="text-[11px] text-[color:var(--muted)]">
                        Saving {pendingCount} kernel{pendingCount === 1 ? "" : "s"}…
                    </span>
                ) : null}
                {phase === "canvas" ? (
                    <span className="text-[11px] text-[color:var(--muted)]">
                        Saving canvas template…
                    </span>
                ) : null}
                {error ? (
                    <span className="text-[11px] text-red-400">{error}</span>
                ) : null}
                <div className="flex items-center justify-end">
                    <button
                        type="submit"
                        disabled={busy}
                        className="h-[30px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer"
                    >
                        {phase === "kernels" ? "Saving kernels…"
                            : phase === "canvas" ? "Saving canvas…"
                            : busy ? "Saving…" : "Save canvas"}
                    </button>
                </div>
            </form>
        </OverlayDialog>
    );
}

// Inline progress meter for the kernels-first phase of the save
// dialog. Single bar split into green (saved), pulsing ink
// (running), and red (errored) segments + a counter line. The user
// sees real motion as each sub-agent lands, not just a static "this
// is happening somewhere" message.
function KernelSaveProgress({ progress }) {
    if (!progress) return null;
    const { total, saved, running, errored } = progress;
    const denom = Math.max(1, total);
    const pct = (n) => `${(n / denom) * 100}%`;
    return (
        <div className="flex flex-col gap-[6px]">
            <div className="flex items-center gap-[8px] text-[11px] leading-[1.3] text-[color:var(--muted)]">
                <span>
                    <span className="text-[color:var(--ink)] font-mono">{saved}</span>
                    {" / "}
                    <span className="font-mono">{total}</span>
                    {" kernels saved"}
                </span>
                {running > 0 ? (
                    <span className="text-[color:var(--muted-soft)]">· {running} in flight</span>
                ) : null}
                {errored > 0 ? (
                    <span className="text-[color:var(--red,#c0392b)]">· {errored} errored</span>
                ) : null}
            </div>
            <div className="relative h-[6px] w-full overflow-hidden rounded-[1px] bg-[color:var(--line-soft)]">
                <div
                    className="absolute inset-y-0 left-0 bg-[color:var(--green)] transition-[width] duration-300 ease-out"
                    style={{ width: pct(saved) }}
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-y-0 animate-pulse bg-[color:var(--ink)] transition-[width,left] duration-300 ease-out"
                    style={{ left: pct(saved), width: pct(running) }}
                    aria-hidden="true"
                />
                {errored > 0 ? (
                    <div
                        className="absolute inset-y-0 bg-[color:var(--red,#c0392b)] transition-[width,left] duration-300 ease-out"
                        style={{
                            left: pct(saved + running),
                            width: pct(errored),
                        }}
                        aria-hidden="true"
                    />
                ) : null}
            </div>
            <span className="text-[10px] text-[color:var(--muted-soft)]">
                Each canvas card also shows its progress.
            </span>
        </div>
    );
}

function CardStatusBadge({ status }) {
    if (!status) return null;
    if (status.status === "running") {
        return (
            <span className="flex items-center gap-[6px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                <span className="inline-block h-[7px] w-[7px] animate-pulse rounded-full bg-[color:var(--muted)]" aria-hidden="true" />
                <span>saving as <span className="font-mono normal-case tracking-normal text-[color:var(--ink)]">/{status.command}</span>…</span>
            </span>
        );
    }
    if (status.status === "saved") {
        return (
            <span className="flex flex-col items-start gap-[3px] text-[color:var(--muted-soft)]">
                <span className="flex items-center gap-[6px] uppercase tracking-[0.14em]">
                    <span className="text-[color:var(--green)]" aria-hidden="true">✓</span>
                    <span>saved as <span className="font-mono normal-case tracking-normal text-[color:var(--ink)]">/{status.command}</span></span>
                </span>
                {status.warning ? (
                    <span role="status" className="normal-case tracking-normal text-[color:var(--muted)]">
                        {status.warning}
                    </span>
                ) : null}
            </span>
        );
    }
    if (status.status === "error") {
        return (
            <span className="flex flex-wrap items-baseline gap-[6px]">
                <span className="uppercase tracking-[0.14em] text-[color:var(--red,#c0392b)]" aria-hidden="true">⚠ error</span>
                <span className="normal-case text-[color:var(--muted)]">{status.error}</span>
            </span>
        );
    }
    return null;
}

function currentKernelResolver() {
    return kernelResolverForTenant(null);
}

// Compact relative time — "just now" / "5m ago" / "3h ago" / "2d ago".
// Matches the style KernelWidget already uses internally; keeping it
// inline (instead of importing from another module) avoids pulling in
// the rest of KernelWidget's tree just for one helper.
function formatAgo(ts) {
    if (!ts) return "";
    const diff = Date.now() - ts;
    if (diff < 5000) return "just now";
    if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
    return `${Math.round(diff / 86_400_000)}d ago`;
}
